import axiosInstance from "../HelperAxios/axios";
import { buildFileUrl } from "../Utils/fileUrlHelper";

// ─── Conversations ─────────────────────────────────────────────────────────

/** Admin: list all accessible conversations (unassigned + own assigned) */
export const getConversations = (params = {}) =>
  axiosInstance.get("/chat/conversations", { params });

/** Get a single conversation by ID (works for both admin and contact) */
export const getConversation = (id) =>
  axiosInstance.get(`/chat/conversations/${id}`);

/** Contact: get or auto-create the contact's own support conversation */
export const getMyConversation = () =>
  axiosInstance.get("/chat/my-conversation");

/** Admin: find or create a conversation for a specific contact by their ID */
export const getOrCreateConversationForContact = (contactId) =>
  axiosInstance.get(`/chat/conversations/by-contact/${contactId}`);

// ─── Messages ──────────────────────────────────────────────────────────────

/** Get paginated message history for a conversation */
export const getMessages = (conversationId, page = 1, limit = 30) =>
  axiosInstance.get(`/chat/conversations/${conversationId}/messages`, {
    params: { page, limit },
  });

/** REST fallback for sending a message (prefer WebSocket send_message event) */
export const sendMessageRest = (conversationId, data) =>
  axiosInstance.post(`/chat/conversations/${conversationId}/messages`, data);

// ─── Assignment & Status ───────────────────────────────────────────────────

/** Admin: assign a conversation to a specific admin user */
export const assignConversation = (conversationId, adminId) =>
  axiosInstance.put(`/chat/conversations/${conversationId}/assign`, { adminId });

/** Admin: close a conversation */
export const closeConversation = (conversationId) =>
  axiosInstance.put(`/chat/conversations/${conversationId}/close`);

// ─── File upload ───────────────────────────────────────────────────────────

/** Upload a file and get back { fileUrl, fileName, fileMime } */
export const uploadFile = (conversationId, file) => {
  const form = new FormData();
  form.append("file", file);
  return axiosInstance.post(
    `/chat/conversations/${conversationId}/upload`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

/**
 * Resolve chat attachment path — same rules as profile pictures (API_URL_PHOTO).
 * Backend stores `/public/filename.ext`; base URL already includes `/public/`.
 */
export const resolveChatFileUrl = buildFileUrl;

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Format a timestamp into a short relative label (e.g. "now", "3m", "2h") */
export const formatChatTime = (isoString) => {
  if (!isoString) return "";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

/** Short label for a message in the live-chats preview line */
export const messagePreviewText = (message) => {
  if (!message) return null;
  const content = message.content?.trim();
  if (content) {
    return content.length > 80 ? `${content.slice(0, 80)}…` : content;
  }
  if (message.fileName) return `📎 ${message.fileName}`;
  if (message.fileUrl) return "📎 Attachment";
  return "New message";
};

const statusPreview = (status) => {
  if (status === "closed") return "Conversation closed";
  if (status === "assigned") return "Assigned";
  return "Open";
};

/** Map a ChatConversation API object to ExchangeLiveChatsCard item shape */
export const mapConversationToItem = (conv, lastMessage) => ({
  key: conv.id,
  name: conv.contact?.name
    ? `${conv.contact.name}${conv.contact.lastName ? " " + conv.contact.lastName : ""}`
    : `Contact #${conv.contact?.id ?? "?"}`,
  preview:
    messagePreviewText(lastMessage) ?? statusPreview(conv.status),
  time: formatChatTime(
    lastMessage?.createdAt || conv.lastMessageAt
  ),
  unreadCount: conv.unreadCountAdmin || 0,
});
